import { AccordionItem, Button, Flex, Modal, Text as PText, useBrand } from '@cof/plastic-components';
import PropTypes from 'prop-types';
import HowDoesItWork from './HowDoesItWork';
import styled, { css } from 'styled-components';
import { useObStartData } from '../../../../data/data';
import { withText } from '../../../../text/textStore';
import { addNewRelicPageAction } from '../../../../utilities/newRelic';
import { useEffect } from 'react';
import { verifyUrlBuilder } from '../../../../utilities/verifyUrlBuilder';
import { safeNavigate } from '../../../../utilities/safeNavigate';
import { CONSUMER_NAMES } from '../../../../utilities/constants';
import ExperimentLockGraphicBlack from './ExperimentLockGraphicBlack';

export const BorderedFlex = styled(Flex)`
    ${({ theme }: any) => css`
        border: 0.1rem solid ${theme.colors.global.grey200};
        justify-content: center;
        align-items: center;
        border-radius: 16px;
    `}
`;

const AccordionContentWrapper = styled.div`
    ${({ theme }: any) => css`
        & > div {
            padding-left: 0;
            padding-right: 0;
        }
        [data-qa-id='how-does-it-work-accordion'] {
            padding-top: 0;
            ${theme.breakpoints.mq.mobileMax} {
                padding-bottom: 16px;
            }
            ${theme.breakpoints.mq.desktopMin} {
                padding-bottom: 24px;
            }
        }
        [data-qa-id='accordion-item-trigger'] {
            padding-left: 0;
            padding-right: 0;
        }
        [data-qa-id='accordion-item-content'] {
            padding-left: 0;
            padding-right: 0;
        }
    `}
`;

const getRedirectParams = (params: any) => {
    const quotation = params.get('quotation');
    const productCode = params.get('p');
    const s = params.get('s');

    return {
        quotation,
        ...(productCode && { productCode }),
        ...(s && { s })
    };
};

const OpenBankingPostSubmitModal = ({ agreementUrl, Text, Text: text }: any) => {
    const brand = useBrand();
    const agreementUrlParams = new URLSearchParams(agreementUrl.split('?')[1]);
    const redirectParams = getRedirectParams(agreementUrlParams);
    const consumerName = CONSUMER_NAMES[brand];

    const [data, loading, { response, error }] = useObStartData(redirectParams);
    const timeoutDuration = 15 * 60 * 1000; // 15 minutes

    useEffect(() => {
        const timeoutTimer = setTimeout(() => {
            addNewRelicPageAction('OB Post Submit Modal - Redirected to Agreements', {
                reason: 'user_timed_out',
                consumerName: consumerName,
                hasConnectionUrl: true
            });

            const newAgreementUrl = new URL(agreementUrl);
            newAgreementUrl.searchParams.append('obSkipped', 'true');
            window.location.assign(newAgreementUrl);
        }, timeoutDuration);

        return () => clearTimeout(timeoutTimer);
    }, []);

    useEffect(() => {
        if (consumerName && data !== 'Session expired' && data?.connectionUrl && !loading) {
            addNewRelicPageAction('OB Post Submit Modal - Success', {
                consumerName,
                hasConnectionUrl: true
            });
        }
    }, [consumerName, data, loading]);

    useEffect(() => {
        if (error || (response && data !== 'Session expired' && !data?.connectionUrl)) {
            addNewRelicPageAction('OB Post Submit Modal - Redirected to Agreements', { reason: 'technical_error' });
            window.location.assign(agreementUrl);
        }
    }, [error, response]);

    if (data === 'Session expired') {
        const redirectToVerifyUrl = verifyUrlBuilder({ ...redirectParams, brand, consumerName });
        addNewRelicPageAction('OB Post Submit Modal - Redirected to Verify', { reason: 'session_expired' });
        window.location.assign(redirectToVerifyUrl.toString());
        return null;
    }

    const declineOb = () => {
        addNewRelicPageAction('OB Post Submit Modal - Redirected to Agreements', {
            reason: 'customer_declined',
            consumerName: consumerName,
            hasConnectionUrl: true
        });

        const newAgreementUrl = new URL(agreementUrl);
        newAgreementUrl.searchParams.append('obSkipped', 'true');
        window.location.assign(newAgreementUrl);
    };

    const linkBankAccount = () => safeNavigate(data?.connectionUrl);

    if (data?.connectionUrl) {
        return (
            <>
                <Modal
                    isOpen={true}
                    // @ts-expect-error TS2353: Object literal may only specify known properties, and 'm' does not exist in type 'WidthProps<Require
                    modalWrapperProps={{ m: 'sm' }}
                    allowDismiss={false}
                    data-qa-id="open-banking-post-submit-modal"
                >
                    <Flex justifyContent="center">
                        <ExperimentLockGraphicBlack data-qm-allow />
                    </Flex>
                    {/* @ts-expect-error TS2769: No overload matches this call. */}
                    <Flex flexDirection="column" my="2">
                        <PText fontWeight="medium" size="large">
                            <Text path="Heading" />
                        </PText>
                        {/* @ts-expect-error TS2322: Type '"2"' is not assignable to type 'ResponsiveValue<AllowedSpace, DefaultTheme> | undefined'. */}
                        <PText mt="2">
                            <Text path="Body" />
                        </PText>
                    </Flex>
                    <AccordionContentWrapper>
                        <AccordionItem
                            heading={text({ path: 'Accordion' })}
                            px={0}
                            data-qa-id="post-submit-ob-modal-accordion"
                            data-qm-allow
                        >
                            <HowDoesItWork />
                        </AccordionItem>
                    </AccordionContentWrapper>
                    <Flex alignItems="center" flexDirection="column">
                        <Button mt={[1, 2]} onClick={linkBankAccount} data-qa-id="submit-ob-btn" data-qm-allow>
                            <Text path="LinkMyBankAccount" />
                        </Button>
                        <Button
                            mt={[1, 2]}
                            variant="secondary"
                            onClick={declineOb}
                            data-qa-id="decline-ob-btn"
                            data-qm-allow
                        >
                            <Text path="NoThanksSkip" />
                        </Button>
                    </Flex>
                </Modal>
            </>
        );
    }
};

const OpenBankingPostSubmitModalWithText = withText('OpenBankingPostSubmitModal')(OpenBankingPostSubmitModal);

OpenBankingPostSubmitModal.propTypes = {
    agreementUrl: PropTypes.string,
    Text: PropTypes.func.isRequired
};

export default OpenBankingPostSubmitModalWithText;
